import { Button } from "primereact/button";
import { useState, useRef, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Toast, ToastMessage } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import RawMaterialService from "../../../services/RawMaterials/RawMaterialService";
import ProductService from "../../../services/products/products.service";

interface RawMaterialSearchProps {
  onSearch: (keyword: string) => void;
  showAddModal: boolean;
  setShowAddModal: (show: boolean) => void;
  existingMaterials: { name: string }[];
  onSaved?: () => void | Promise<void>;
}

const RawMaterialSearch: React.FC<RawMaterialSearchProps> = ({
  onSearch,
  showAddModal,
  setShowAddModal,
  existingMaterials,
  onSaved,
}) => {
  const [selectedKeyword, setSelectedKeyword] = useState("");
  const [name, setName] = useState("");
  const [hsn, setHsn] = useState("");
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [measurementTypeId, setMeasurementTypeId] = useState<number | null>(null);
  const [productIds, setProductIds] = useState<number[]>([]);
  const [suggestedNames, setSuggestedNames] = useState<string[]>([]);
  const [nameError, setNameError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [measurementError, setMeasurementError] = useState("");
  const [productError, setProductError] = useState("");
  const [categories, setCategories] = useState<any[]>([]);
  const [measurementTypes, setMeasurementTypes] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const toast = useRef<Toast>(null);

  const showMessage = (label: string, message: string, severity: ToastMessage["severity"]) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const handleSearch = () => {
    onSearch(selectedKeyword);
  };

  const resetSearchFields = () => {
    setSelectedKeyword("");
    onSearch("");
  };

  useEffect(() => {
    const fetchDropdownData = async () => {
      const categoriesResponse = await RawMaterialService.RMCategories();
      const measurementTypesResponse = await RawMaterialService.RMMeasurementtypes();
      const productsResponse = await ProductService.getAllAdminProducts();

      if (categoriesResponse) {
        setCategories(
          categoriesResponse.map((c: any) => ({
            label: c.categoryName,
            value: c.categoryID,
          }))
        );
      }

      if (measurementTypesResponse) {
        setMeasurementTypes(
          measurementTypesResponse.map((m: any) => ({
            label: m.measurementTypeName,
            value: m.measurementTypeID,
          }))
        );
      }

      if (productsResponse) {
        setProducts(
          productsResponse.map((p: any) => ({
            label: p.productName || p.name || p.title || `Product-${p.productID || p.productId || p.id || p.Id}`,
            value: p.productID || p.productId || p.id || p.Id,
          }))
        );
      }
    };

    fetchDropdownData();
  }, []);

  const normalize = (str: string) => str.replace(/\s+/g, "").toLowerCase();

  const isDuplicateName = (value: string): boolean => {
    return existingMaterials.some((m) => normalize(m.name) === normalize(value));
  };

  const highlightMatch = (suggestion: string, input: string) => {
    if (!input.trim()) return <span>{suggestion}</span>;
    const escapedInput = input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedInput})`, "gi");
    const parts = suggestion.split(regex);
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <strong key={i} style={{ color: "#2d6a4f" }}>
              {part}
            </strong>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  const handleOnSuccess = async () => {
    let hasError = false;

    if (!name.trim()) {
      setNameError("Name is required.");
      hasError = true;
    } else if (isDuplicateName(name)) {
      setNameError("A raw material with this name already exists.");
      hasError = true;
    } else {
      setNameError("");
    }

    if (!categoryId) {
      setCategoryError("Category is required.");
      hasError = true;
    } else {
      setCategoryError("");
    }

    if (!measurementTypeId) {
      setMeasurementError("Measurement Type is required.");
      hasError = true;
    } else {
      setMeasurementError("");
    }

    if (!productIds || productIds.length === 0) {
      setProductError("At least one product is required.");
      hasError = true;
    } else {
      setProductError("");
    }

    if (hasError) return;

    try {
      const response = await RawMaterialService.addRawMaterial({
        Name: name.trim(),
        HSNCode: hsn.trim(),
        RMCategoryId: Number(categoryId) || 0,
        MeasurementTypeId: Number(measurementTypeId) || 0,
        ProductIds: productIds,
        productIds: productIds,
      });

      if (response && (response.isSuccess || response.success)) {
        showMessage("Add Raw Material", "Raw Material added successfully", "success");
        handleClose();
        await onSaved?.();
      } else {
        showMessage("Add Raw Material", response?.message || "Failed to add raw material", "error");
      }
    } catch (error) {
      console.error("Error adding raw material:", error);
      showMessage("Add Raw Material", "Unexpected error occurred.", "error");
    }
  };

  const handleClose = () => {
    setShowAddModal(false);
    setName("");
    setHsn("");
    setCategoryId(null);
    setMeasurementTypeId(null);
    setProductIds([]);
    setNameError("");
    setCategoryError("");
    setMeasurementError("");
    setProductError("");
    setSuggestedNames([]);
  };

  return (
    <div className="col-12">
      <style>{`
          .users-search-container {
              border: 1px solid rgba(76, 175, 80, 0.15);
              background: #ffffff;
              border-radius: 12px;
          }
          .users-search-container .search-panel-header {
              display: flex;
              align-items: center;
              gap: 0.5rem;
              margin-bottom: 1rem;
              padding-bottom: 0.75rem;
              border-bottom: 1px solid #f0f0f0;
          }
          .users-search-container .search-panel-header i {
              color: #4CAF50;
              font-size: 1rem;
          }
          .users-search-container .search-panel-header span {
              font-weight: 600;
              color: #374151;
              font-size: 0.9rem;
          }
          .users-search-container .p-dropdown,
          .users-search-container .p-inputtext,
          .users-search-container .p-calendar {
              height: 40px !important;
              min-height: 40px !important;
              font-size: 0.9rem !important;
              border-radius: 8px !important;
              border-color: #e0e0e0 !important;
              width: 100% !important;
          }
          .users-search-container .p-button {
              height: 40px !important;
              border-radius: 8px !important;
          }
          .filter-btn-custom {
              background: #4CAF50 !important;
              border: none !important;
              padding: 0 1.5rem !important;
              font-weight: 600 !important;
              border-radius: 8px !important;
              box-shadow: 0 4px 12px rgba(76, 175, 80, 0.2) !important;
              transition: all 0.2s ease;
          }
          .filter-btn-custom:hover {
              transform: translateY(-1px);
              box-shadow: 0 6px 16px rgba(76, 175, 80, 0.3) !important;
          }
      `}</style>
      <div className="users-search-container surface-card p-3 shadow-2 mt-3 mb-4">
        <div className="search-panel-header">
          <i className="pi pi-filter-fill"></i>
          <span>Filter Raw Materials</span>
        </div>

        <div className="grid">
          <div className="col-12 sm:col-4 md:col-3">
            <span className="p-input-icon-left w-full">
              <i className="pi pi-search" />
              <InputText
                type="search"
                value={selectedKeyword}
                onChange={(e) => setSelectedKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                placeholder="Keyword Search"
              />
            </span>
          </div>

          <div className="col-12 sm:col-8 md:col-9 flex gap-2 justify-content-end align-items-center">
            <Button onClick={handleSearch} className="filter-btn-custom" style={{ minWidth: "100px" }}>
              Filter
            </Button>
            <Button onClick={resetSearchFields} severity="secondary" outlined style={{ minWidth: "100px" }}>
              Reset
            </Button>
          </div>
        </div>

        <Dialog
          visible={showAddModal}
          header="Create Raw Material"
          style={{ width: "100%", maxWidth: "650px" }}
          modal
          onHide={handleClose}
          className="minimal-batch-dialog"
          footer={
            <div className="minimal-dialog-footer">
              <Button label="Cancel" onClick={handleClose} className="btn-minimal-cancel" />
              <Button label="Save Raw Material" onClick={handleOnSuccess} className="btn-minimal-save" />
            </div>
          }
        >
          <style>{`
            .minimal-batch-dialog .p-dialog-header { background: #ffffff; padding: 2rem 2.5rem 1rem; border-bottom: none; }
            .minimal-batch-dialog .p-dialog-title { font-weight: 600; color: #111827; font-size: 1.5rem; letter-spacing: -0.02em; }
            .minimal-batch-dialog .p-dialog-content { padding: 0 !important; background: #ffffff; }
            .batch-custom-scroll { max-height: 65vh; overflow-y: auto; padding: 0 2.5rem 2rem !important; }
            .minimal-form-field { margin-bottom: 1.5rem; }
            .minimal-form-field label { font-size: 0.85rem; font-weight: 500; color: #6b7280; display: block; margin-bottom: 0.5rem; text-transform: uppercase; }
            .minimal-form-field .p-dropdown, .minimal-form-field .p-calendar .p-inputtext, .minimal-form-field .p-inputtext, .minimal-form-field .p-multiselect, .minimal-form-field .p-inputnumber {
                background: #f9fafb !important; border: 1px solid #f3f4f6 !important; border-radius: 12px !important; width: 100%; font-size: 0.95rem; color: #1f2937;
            }
            .minimal-form-field .p-inputtext, .minimal-form-field .p-dropdown .p-dropdown-label, .minimal-form-field .p-multiselect .p-multiselect-label {
                padding: 0.75rem 1rem !important;
            }
            .minimal-form-field .p-dropdown:hover, .minimal-form-field .p-inputtext:hover { background: #ffffff !important; border-color: #d1d5db !important; }
            .minimal-form-field .p-dropdown.p-focus, .minimal-form-field .p-inputtext:focus { background: #ffffff !important; border-color: #10B981 !important; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important; }
            .minimal-dialog-footer { display: flex; justify-content: flex-end; gap: 1rem; padding: 1.5rem 2.5rem; background: #ffffff; border-top: 1px solid #f3f4f6; }
            .btn-minimal-cancel { background: transparent !important; color: #6b7280 !important; border: none !important; font-weight: 500; padding: 0.75rem 1.5rem !important; border-radius: 999px !important; }
            .btn-minimal-save {
                background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%) !important;
                color: #ffffff !important;
                border: none !important;
                font-weight: 600 !important;
                padding: 0.75rem 2.5rem !important;
                border-radius: 999px !important;
                box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
                transition: all 0.3s ease !important;
            }
            .btn-minimal-save:hover {
                transform: translateY(-2px) !important;
                box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
            }
            .required-star { color: #ef4444; margin-left: 2px; }
          `}</style>
          <div className="batch-custom-scroll">
            <div className="grid p-fluid mt-2">
              <div className="col-12 md:col-6 minimal-form-field" style={{ position: "relative" }}>
                <label>
                  Raw Material Name <span className="required-star">*</span>
                </label>
                <InputText
                  value={name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setName(value);

                    if (!value.trim()) {
                      setNameError("Name is required.");
                      setSuggestedNames([]);
                    } else if (isDuplicateName(value)) {
                      setNameError("A raw material with this name already exists.");
                      setSuggestedNames([]);
                    } else {
                      setNameError("");
                      const matches = existingMaterials
                        .filter((m) => normalize(m.name).includes(normalize(value)))
                        .map((m) => m.name)
                        .slice(0, 5);
                      setSuggestedNames(matches);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setSuggestedNames([]), 200);
                  }}
                  placeholder="Enter Raw Material Name"
                  autoFocus
                  className={nameError ? "p-invalid" : ""}
                />

                {suggestedNames.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      backgroundColor: "white",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      marginTop: "2px",
                    }}
                  >
                    <div
                      style={{
                        padding: "6px 12px",
                        fontSize: "11px",
                        color: "#6c757d",
                        borderBottom: "1px solid #f0f0f0",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Similar names already exist
                    </div>

                    {suggestedNames.map((suggestion, index) => (
                      <div
                        key={index}
                        style={{
                          padding: "8px 12px",
                          fontSize: "13px",
                          color: "#495057",
                          backgroundColor: "white",
                          borderBottom: index < suggestedNames.length - 1 ? "1px solid #f8f9fa" : "none",
                          cursor: "default",
                          userSelect: "none",
                        }}
                      >
                        <i
                          className="pi pi-info-circle"
                          style={{ color: "#f59e0b", marginRight: "8px", fontSize: "12px" }}
                        />
                        {highlightMatch(suggestion, name)}
                      </div>
                    ))}
                  </div>
                )}

                {nameError && <small className="p-error">{nameError}</small>}
              </div>

              <div className="col-12 md:col-6 minimal-form-field">
                <label>HSN Code</label>
                <InputText value={hsn} onChange={(e) => setHsn(e.target.value)} placeholder="Enter HSN Code" />
              </div>

              <div className="col-12 md:col-6 minimal-form-field">
                <label>
                  Category <span className="required-star">*</span>
                </label>
                <Dropdown
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.value);
                    if (e.value) setCategoryError("");
                  }}
                  options={categories}
                  placeholder="Select Category"
                  filter
                  filterMatchMode="startsWith"
                  showFilterClear
                  className={categoryError ? "p-invalid" : ""}
                />
                {categoryError && <small className="p-error">{categoryError}</small>}
              </div>

              <div className="col-12 md:col-6 minimal-form-field">
                <label>
                  Measurement Type <span className="required-star">*</span>
                </label>
                <Dropdown
                  value={measurementTypeId}
                  onChange={(e) => {
                    setMeasurementTypeId(e.value);
                    if (e.value) setMeasurementError("");
                  }}
                  options={measurementTypes}
                  placeholder="Select Measurement"
                  className={measurementError ? "p-invalid" : ""}
                />
                {measurementError && <small className="p-error">{measurementError}</small>}
              </div>

              <div className="col-12 minimal-form-field">
                <label>
                  Associated Products <span className="required-star">*</span>
                </label>
                <MultiSelect
                  value={productIds}
                  onChange={(e) => {
                    setProductIds(Array.isArray(e.value) ? e.value.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0) : []);
                    if (e.value && e.value.length > 0) setProductError("");
                  }}
                  options={products}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select Products"
                  filter
                  className={productError ? "p-invalid" : ""}
                  display="chip"
                />
                {productError && <small className="p-error">{productError}</small>}
              </div>
            </div>
          </div>
        </Dialog>
        <Toast ref={toast} />
      </div>
    </div>
  );
};

export default RawMaterialSearch;
